// src/services/calendarService.js
import { supabase } from '../lib/supabase';
import { withTimeout } from '../lib/supabaseClient';
import { apiFetch } from './apiClient';

// SECURITY: Client secrets removed - all token operations now handled on backend
const API_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';

export const calendarService = {
  // Mint a single-use connect token for calendar OAuth initiation
  async mintConnectToken(provider) {
    return apiFetch('/v1/oauth/connect-token', {
      method: 'POST',
      body: { provider }
    });
  },

  // Generate Google OAuth URL for calendar access (backend flow)
  async getGoogleAuthUrl() {
    const { token } = await this.mintConnectToken('google');
    return `${API_URL}/v1/calendars/google/connect?token=${token}`;
  },

  // Generate Microsoft OAuth URL for calendar access
  async getMicrosoftAuthUrl() {
    const { token } = await this.mintConnectToken('microsoft');
    return `${API_URL}/v1/calendars/microsoft/connect?token=${token}`;
  },

  // Connect an Apple (iCloud) calendar. Apple has no OAuth redirect — the user
  // supplies an Apple ID + app-specific password, which we POST directly.
  // Returns { connected, provider, replaced, calendars }.
  async connectApple(appleId, appPassword) {
    // NOT routed through apiClient on purpose: this endpoint returns 401 for
    // *bad Apple credentials* (RFC-9457 invalid-credentials), which is not a
    // dead Vennio session — running it through apiFetch's 401 recovery would
    // wrongly sign the user out on a mistyped app password. Keep the direct
    // fetch + bespoke status handling.
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${API_URL}/v1/calendars/apple/connect`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ apple_id: appleId, app_password: appPassword })
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      // API returns RFC-9457 problem shape (title/detail) on 401/422.
      const err = new Error(data.detail || data.title || data.message || 'Failed to connect Apple Calendar');
      err.status = response.status;
      throw err;
    }
    return data;
  },

  // Exchange authorization code for tokens (DEPRECATED - only used by old client-side OAuth flow)
  // New Google OAuth uses backend flow, this is kept for backward compatibility
  async exchangeCodeForTokens(code) {
    try {
      const response = await fetch('https://oauth2.googleapis.com/token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: new URLSearchParams({
          code,
          client_id: GOOGLE_CLIENT_ID,
          client_secret: GOOGLE_CLIENT_SECRET,
          redirect_uri: REDIRECT_URI,
          grant_type: 'authorization_code'
        })
      });

      if (!response.ok) {
        throw new Error('Failed to exchange code for tokens');
      }

      const data = await response.json();
      return {
        accessToken: data.access_token,
        refreshToken: data.refresh_token,
        expiresAt: new Date(Date.now() + data.expires_in * 1000)
      };
    } catch (error) {
      console.error('Error exchanging code:', error);
      throw error;
    }
  },

  // Save calendar connection to database (DEPRECATED - only used by old client-side OAuth flow)
  // New Google OAuth uses backend flow, this is kept for backward compatibility
  async saveCalendarConnection(userId, tokens) {
    try {
      const { data, error } = await supabase
        .from('calendar_connections')
        .upsert({
          user_id: userId,
          provider: 'google',
          access_token: tokens.accessToken, // In production, encrypt this!
          refresh_token: tokens.refreshToken,
          token_expires_at: tokens.expiresAt.toISOString(),
          is_active: true
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error saving calendar connection:', error);
      throw error;
    }
  },

  // Get ALL calendar connections for user with health status (supports multi-provider)
  async getCalendarConnections(userId) {
    try {
      // First try to get health data from API
      try {
        const result = await apiFetch('/v1/calendars/connections');
        return result.connections || [];
      } catch (apiError) {
        console.warn('Failed to fetch from API, falling back to direct query:', apiError);
      }

      // Fallback to direct Supabase query (without health data)
      const fetchPromise = supabase
        .from('calendar_connections')
        .select('id, provider, synced_at, created_at, is_active')
        .eq('user_id', userId)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      const { data, error } = await withTimeout(fetchPromise, 3000);

      if (error) {
        throw error;
      }

      // Return connections without health data
      return (data || []).map(conn => ({
        ...conn,
        health: 'unknown',
        token_status: { status: 'unknown', message: 'Health check unavailable' }
      }));
    } catch (error) {
      console.error('Error getting calendar connections:', error);
      return [];
    }
  },

  // Get calendar connection for user (DEPRECATED - use getCalendarConnections for multi-provider)
  // This returns the first connection found, for backward compatibility
  async getCalendarConnection(userId) {
    try {
      const connections = await this.getCalendarConnections(userId);
      // Return first active connection (most recent if multiple exist)
      return connections[0] || null;
    } catch (error) {
      console.error('Error getting calendar connection:', error);
      return null;
    }
  },

  // REMOVED: Token refresh is now handled securely on the backend
  // The following functions have been removed for security:
  // - refreshGoogleAccessToken()
  // - refreshMicrosoftAccessToken()
  // - refreshAccessToken()
  // - getValidAccessToken()
  //
  // Token refresh now happens automatically on the backend during sync operations.
  // Client secrets are no longer exposed to the frontend.

  // Fetch calendar events from Google Calendar API
  async fetchGoogleCalendarEvents(accessToken, timeMin, timeMax, calendarId = 'primary') {
    try {
      const params = new URLSearchParams({
        timeMin: timeMin.toISOString(),
        timeMax: timeMax.toISOString(),
        singleEvents: true,
        orderBy: 'startTime'
      });

      const response = await fetch(
        `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(calendarId)}/events?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch Google calendar events for ${calendarId}`);
      }

      const data = await response.json();
      return data.items || [];
    } catch (error) {
      console.error(`Error fetching Google calendar events for ${calendarId}:`, error);
      throw error;
    }
  },

  // Fetch calendar events from Microsoft Graph API
  async fetchMicrosoftCalendarEvents(accessToken, timeMin, timeMax, calendarId = null) {
    try {
      // Microsoft Graph API uses different date format (ISO 8601)
      const params = new URLSearchParams({
        startDateTime: timeMin.toISOString(),
        endDateTime: timeMax.toISOString(),
        $top: '2500',
        $orderby: 'start/dateTime'
      });

      // Microsoft doesn't use "primary" - use default calendar endpoint instead
      // If calendarId is "primary" or null, use /calendar (default)
      // Otherwise use the specific calendar ID
      const calendarPath = (calendarId && calendarId !== 'primary')
        ? `/calendars/${calendarId}`
        : '/calendar';

      const response = await fetch(
        `https://graph.microsoft.com/v1.0/me${calendarPath}/events?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
            'Prefer': 'outlook.timezone="UTC"'
          }
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Microsoft Graph API error:', response.status, errorText);
        throw new Error(`Failed to fetch Microsoft calendar events for ${calendarId || 'default'}`);
      }

      const data = await response.json();

      // Normalize Microsoft events to Google Calendar format
      const normalizedEvents = (data.value || []).map(event => ({
        id: event.id,
        summary: event.subject,
        start: {
          dateTime: event.start.dateTime,
          timeZone: event.start.timeZone || 'UTC'
        },
        end: {
          dateTime: event.end.dateTime,
          timeZone: event.end.timeZone || 'UTC'
        },
        status: event.isCancelled ? 'cancelled' : 'confirmed',
        transparency: event.showAs === 'free' ? 'transparent' : 'opaque'
      }));

      return normalizedEvents;
    } catch (error) {
      console.error(`Error fetching Microsoft calendar events for ${calendarId || 'default'}:`, error);
      throw error;
    }
  },

  // Fetch calendar events (provider-aware)
  async fetchCalendarEvents(accessToken, timeMin, timeMax, calendarId, provider = 'google') {
    if (provider === 'microsoft') {
      return this.fetchMicrosoftCalendarEvents(accessToken, timeMin, timeMax, calendarId);
    } else {
      return this.fetchGoogleCalendarEvents(accessToken, timeMin, timeMax, calendarId);
    }
  },

  // Get selected calendars for a user (optionally filtered by provider)
  async getSelectedCalendars(userId, provider = null) {
    try {
      let query = supabase
        .from('calendar_selections')
        .select('calendar_id, calendar_name, is_selected, provider')
        .eq('user_id', userId)
        .eq('is_selected', true);

      // Filter by provider if specified
      if (provider) {
        query = query.eq('provider', provider);
      }

      const { data, error } = await query;

      if (error) throw error;

      // If no calendars are selected, default to primary
      if (!data || data.length === 0) {
        return [{
          calendar_id: 'primary',
          calendar_name: 'Primary Calendar',
          is_selected: true,
          provider: provider || 'google'
        }];
      }

      return data;
    } catch (error) {
      console.error('Error fetching selected calendars:', error);
      // Fallback to primary calendar on error
      return [{
        calendar_id: 'primary',
        calendar_name: 'Primary Calendar',
        is_selected: true,
        provider: provider || 'google'
      }];
    }
  },

  // Sync availability to database (SECURE: delegates to backend API)
  async syncAvailability(userId, connection, daysAhead = 60) {
    try {
      const provider = connection.provider || 'google';
      console.log(`Syncing calendar for user ${userId} using provider: ${provider}`);

      // Call secure backend sync endpoint
      const result = await apiFetch(
        `/v1/calendars/sync?provider=${provider}&days_ahead=${daysAhead}`,
        { method: 'POST' }
      );

      // Extract results for this specific provider
      const providerResult = result.providers?.find(p => p.provider === provider);

      if (!providerResult || providerResult.status !== 'fulfilled') {
        throw new Error(providerResult?.error || 'Sync failed for provider');
      }

      return {
        success: true,
        blocksCount: providerResult.data?.blocksCount || 0,
        calendarsCount: providerResult.data?.calendarsCount || 0
      };
    } catch (error) {
      console.error('Error syncing availability:', error);
      throw error;
    }
  },

  // Sync all connected calendar providers for a user (SECURE: delegates to backend API)
  async syncAllProviders(userId, daysAhead = 60) {
    try {
      console.log(`Syncing all providers for user ${userId}`);

      // Call secure backend sync endpoint (without provider param = sync all)
      const result = await apiFetch(`/v1/calendars/sync?days_ahead=${daysAhead}`, {
        method: 'POST'
      });

      // Transform backend response to match expected format
      const providers = result.providers.map(p => ({
        provider: p.provider,
        success: p.status === 'fulfilled',
        blocksCount: p.data?.blocksCount || 0,
        calendarsCount: p.data?.calendarsCount || 0,
        error: p.error
      }));

      return {
        success: result.success,
        providers,
        totalBlocks: result.total_blocks,
        totalCalendars: result.total_calendars,
        successCount: result.synced_providers,
        failureCount: result.failed_providers
      };
    } catch (error) {
      console.error('Error syncing all providers:', error);
      throw error;
    }
  },

  // Get availability blocks for calendar display
  async getAvailabilityBlocks(userId, startDate, endDate) {
    try {
      const { data, error } = await supabase
        .from('availability_blocks')
        .select('*')
        .eq('user_id', userId)
        .gte('start_time', startDate.toISOString())
        .lte('end_time', endDate.toISOString())
        .order('start_time', { ascending: true});

      if (error) throw error;

      return data || [];
    } catch (error) {
      console.error('Error fetching availability blocks:', error);
      throw error;
    }
  },

  // Disconnect calendar (specific provider or all)
  async disconnectCalendar(userId, provider = null) {
    try {
      if (provider) {
        // Disconnect specific provider
        const { error } = await supabase
          .from('calendar_connections')
          .delete()
          .eq('user_id', userId)
          .eq('provider', provider);

        if (error) throw error;

        // Delete availability blocks for this provider only
        await supabase
          .from('availability_blocks')
          .delete()
          .eq('user_id', userId)
          .eq('provider', provider);

        // Delete calendar selections for this provider
        await supabase
          .from('calendar_selections')
          .delete()
          .eq('user_id', userId)
          .eq('provider', provider);

      } else {
        // Disconnect all providers
        const { error } = await supabase
          .from('calendar_connections')
          .delete()
          .eq('user_id', userId);

        if (error) throw error;

        // Delete all availability blocks
        await supabase
          .from('availability_blocks')
          .delete()
          .eq('user_id', userId);

        // Delete all calendar selections
        await supabase
          .from('calendar_selections')
          .delete()
          .eq('user_id', userId);
      }

      return { success: true, provider: provider || 'all' };
    } catch (error) {
      console.error('Error disconnecting calendar:', error);
      throw error;
    }
  },

  // Calendar write-back on confirmed bookings is handled server-side by
  // createBusinessCalendarEvent() in the API. No client-side event creation needed.
};